import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';

import { IncidentBannerSaveDto } from '../../../../../../src/services/incident-banners/model';
import { AppContext, FormProps } from '../../interfaces';
import { handleFormCancel, saveBanner, updateBanner } from '../../store/incidentBannersSlice';
import { AppDispatch, RootState } from '../../store/store';

import { Wrapper } from './ActionsStyled';
import { DEFAULT_FORM_VALUES } from './index';

interface Props {
    context: AppContext;
}

const getNormalizedData = (content: string, name: string, context: AppContext): IncidentBannerSaveDto => ({
    content: content || '',
    issueId: context.extension.issue.id,
    name,
    projectId: context.extension.project.id,
});

const isEditMode = (activeBannerKey: string | undefined) => !!activeBannerKey;

const Actions: React.FC<Props> = ({ context }) => {
    const { activeBannerKey, canEdit, isLoading } = useSelector((state: RootState) => state.incidentBanners);
    const { getValues, trigger, reset } = useFormContext<FormProps>();
    const dispatch = useDispatch<AppDispatch>();
    const isEdit = useMemo(() => isEditMode(activeBannerKey), [activeBannerKey]);

    const handleCancel = useCallback(() => {
        dispatch(handleFormCancel(isEdit));
        reset(DEFAULT_FORM_VALUES);
    }, [dispatch, isEdit, reset]);

    const saveNewBanner = useCallback(
        (data: IncidentBannerSaveDto) => {
            dispatch(saveBanner(data))
                .unwrap()
                .then(() => reset(DEFAULT_FORM_VALUES))
                .catch(err => console.error(err));
        },
        [dispatch, reset]
    );

    const updateExistingBanner = useCallback(
        (data: IncidentBannerSaveDto) => {
            dispatch(updateBanner(data))
                .unwrap()
                .then(() => reset(DEFAULT_FORM_VALUES))
                .catch(err => console.error(err));
        },
        [dispatch, reset]
    );

    const handleSubmit = useCallback(async () => {
        const areFieldsValid = await trigger();
        const { name, content } = getValues();

        if (areFieldsValid) {
            const data = getNormalizedData(content, name, context);
            isEdit ? updateExistingBanner(data) : saveNewBanner(data);
        }
    }, [trigger, getValues, context, isEdit, saveNewBanner, updateExistingBanner]);

    return (
        <Wrapper>
            {canEdit ?
                <ButtonGroup>
                    <Button
                        isDisabled={isLoading}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        isLoading={isLoading}
                        onClick={handleSubmit}
                        appearance='primary'
                    >
                        {isEdit ? 'Edit' : 'Save'} banner
                    </LoadingButton>
                </ButtonGroup>
            :   <Button onClick={handleCancel}>Close preview</Button>}
        </Wrapper>
    );
};

export default Actions;
